/* eslint-disable jsx-a11y/label-has-for */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import {
  APPLY_USERINPUT_TYPES,
  CHANNEL_TYPES,
  APPLY_ACTION_TYPES,
} from '@bingads-webui-campaign/recommendation-core';
import { EstimationTile } from '@bingads-webui-campaign-react/recommendation-estimation-tile';

import { ErrorMessages } from '@bingads-webui-react/validation';
import { ValidatedInputLite } from '@bingads-webui-react/validation-controls';
import { RadioGroup } from '@bingads-webui-react/radio';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';

import { userInputsMappingConfigForForm } from './user-inputs-mapping-config';
import { LabelDecorator } from './label-decorator';

export class FormActionWithEstimates extends React.PureComponent {
  static propTypes = {
    title: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string,
      label: PropTypes.string,
      text: PropTypes.string,
      value: PropTypes.string,
      enum: PropTypes.array,
      helpTopic: PropTypes.string,
      helpText: PropTypes.string,
      helpQuery: PropTypes.string,
    })).isRequired,
    i18n: PropTypes.PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    estimates: PropTypes.objectOf(PropTypes.any).isRequired,
    track: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired,
    model: PropTypes.objectOf(PropTypes.any).isRequired,
    target: PropTypes.objectOf(PropTypes.any).isRequired,
    rowClass: PropTypes.string.isRequired,
    schema: PropTypes.objectOf(PropTypes.any).isRequired,
    currency: PropTypes.string.isRequired,
    logOpportunityEvent: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
    context: PropTypes.shape({
      Level: PropTypes.string.isRequired,
    }).isRequired,
    guid: PropTypes.string.isRequired,
    logExpInfo: PropTypes.func.isRequired,
  };

  static defaultProps = {
    title: '',
  };

  constructor(props) {
    super(props);

    this.state = {};
    this.validationPropsDict = {};
    this.formattedFieldsDict = {};

    props.logOpportunityEvent({
      opportunityId: props.model.opportunityId,
      campaignId: props.target.CampaignId,
      context: props.context,
    });
    _.each(props.fields, (field) => {
      const {
        type, property, value, items,
      } = field;
      this.formattedFieldsDict[property] = field;

      if (type === 'input') {
        this.state[property] = { radio: items[0], suggested: true };

        // initalize error processor
        const propertySchema = props.schema.properties[property];
        const errorProcessor = errors => _.map(
          errors,
          (oldError, key) => {
            if (key === 'range') {
              return props.i18n.getString(`Validation_Number_Range_Between_${property}`, {
                min: propertySchema.minimum,
                max: propertySchema.maximum,
                currency: props.currency,
              });
            }
            return oldError;
          }
        );
        this.validationPropsDict[property] = {
          i18n: props.i18n,
          schema: props.schema,
          field: property,
          errorProcessor,
          onValidate: this.onValidate(property),
        };
      } else if (type === 'select') {
        this.state[property] = { suggested: true, value };
      }
    });
  }

  onUpdateState = (property, val) => {
    let input = val;

    this.setState((prevState) => {
      input = _.extend({}, prevState[property], val);

      return {
        [property]: input,
      };
    });
  }

  onInputChange = (e) => {
    const { name, value } = e.target;
    const { items } = this.formattedFieldsDict[name];

    this.onUpdateState(name, {
      radio: items[1],
      suggested: false,
      value,
    });
  }

  onRadioChange = property => (item) => {
    const { items } = this.formattedFieldsDict[property];

    this.onUpdateState(property, {
      radio: item,
      suggested: item === items[0],
    });
  }

  onSelectChange = (e) => {
    const { name, value } = e.target;
    const defaultSelectedValue = this.formattedFieldsDict[name].value;

    this.onUpdateState(name, {
      value,
      suggested: value === defaultSelectedValue,
    });
  }

  onValidate = property => ({ messages, isValid }) => {
    this.onUpdateState(property, {
      messages,
      isValid,
    });
  }

  getUserInputs(isApply, returnOnlyWhenModified = false) {
    let modified = false;

    const defaultUserInputObject = {
      OpportunityId: this.props.model.opportunityId,
    };

    const userInputObject = {};

    if (isApply) {
      const odataType = APPLY_USERINPUT_TYPES[this.props.type];
      if (odataType) {
        defaultUserInputObject['@odata.type'] = odataType;
      }

      const config = userInputsMappingConfigForForm[this.props.type];
      _.each(config, ({ property, parser, isFromTarget }, key) => {
        let value;
        if (isFromTarget) {
          value = this.props.target[property];
        } else {
          const propertyState = this.state[property];
          if (propertyState.suggested) {
            value = this.props.model[property];
          } else {
            value = parser(this.props.i18n, propertyState.value);
            modified = true;
          }
        }
        userInputObject[key] = value;
      });
    }

    if (returnOnlyWhenModified && !modified) return null;

    if (modified) {
      return [_.extend({}, defaultUserInputObject, userInputObject)];
    }
    return [defaultUserInputObject];
  }

  buildRequestOptions(isApply) {
    const campaignAdGroups = [{
      Key: this.props.target.CampaignId,
      Value: this.props.target.AdGroupId ? [this.props.target.AdGroupId] : [],
    }];

    const userAction = isApply ? APPLY_ACTION_TYPES.ACCEPT : APPLY_ACTION_TYPES.DISMISS;
    const userInputs = this.getUserInputs(isApply);

    return {
      userInputs,
      userAction,
      type: this.props.type,
      campaignAdGroups,
      channel: CHANNEL_TYPES.COMPETITION,
      context: this.props.context,
      guid: this.props.guid,
    };
  }

  hasError() {
    return _.some(this.state, ({
      suggested,
      isValid,
    }) => (!suggested && isValid === false));
  }

  apply = () => {
    if (this.hasError()) return;

    this.props.logExpInfo('1 recommendation applied');

    const ops = this.buildRequestOptions(true);
    this.props.track(ops);
  }

  dismiss = () => {
    if (this.hasError()) return;

    const ops = this.buildRequestOptions(false);
    this.props.track(ops);
  }

  renderRadioGroupItem = field => (item, index) => {
    const { property, value } = field;
    const propertyState = this.state[property];

    if (index === 0) {
      return this.props.i18n.getString('Details_View_Suggested_Suffix', {
        suggested_value: item,
      });
    }
    return (
      <React.Fragment>
        <ValidatedInputLite
          value={value}
          placeholder={item}
          name={property}
          onChange={this.onInputChange}
          validationProps={this.validationPropsDict[property]}
          className="form-control"
        />
        {!propertyState.suggested &&
          !propertyState.isValid &&
          <ErrorMessages messages={propertyState.messages} />}
      </React.Fragment>);
  };

  renderRadioGroup = field =>
    (<RadioGroup
      items={field.items}
      onChange={this.onRadioChange(field.property)}
      checked={this.state[field.property].radio}
      renderItem={this.renderRadioGroupItem(field)}
    />);

  renderField = (field, index) => {
    let control = (
      <LabelDecorator key={index} label={field.label}>
        <div className="control-text">
          <label className="control-label">{field.text || field.value}</label>
        </div>
      </LabelDecorator>);

    switch (field.type) {
      case 'input': {
        control = (
          <LabelDecorator key={index} label={field.label}>
            {this.renderRadioGroup(field)}
          </LabelDecorator>);
        break;
      }
      case 'select': {
        const options = _.map(field.enum, ({ value, text }, key) => (
          <option key={key} value={value}>{text}</option>
        ));
        control = (
          <LabelDecorator key={index} label={field.label}>
            <select
              value={this.state[field.property].value}
              name={field.property}
              onChange={this.onSelectChange}
              className="form-control"
            >
              {options}
            </select>
          </LabelDecorator>);
        break;
      }
      case 'info':
        control = (
          <div key={index}>
            {field.text}
            <div className="help-text">
              <div className="infomark infoalign" />
              <LearnMoreLink
                text={field.helpText}
                linkText={this.props.i18n.getString('Learn_more_link_text')}
                topic={field.helpQuery}
              />
            </div>
          </div>);
        break;
      default:
        break;
    }
    return control;
  }

  renderFields = fields => _.map(fields, this.renderField)

  render() {
    const {
      i18n,
      title,
      fields,
      rowClass,
      readOnly,
    } = this.props;

    let suggested = true;
    _.each(this.state, (value, property) => {
      if (this.state[property] && this.state[property].suggested === false) {
        suggested = false;
      }
    });

    return (
      <React.Fragment>
        <div className={`panel details-action-col ${rowClass}`} >
          <div className="panel-heading panel-title">{title}</div>
          <div className="panel-body form-horizontal">
            {this.renderFields(fields)}
          </div>
          <div className="panel-footer">
            <button type="button" disabled={readOnly} className="btn btn-primary" onClick={this.apply}>
              {i18n.getString('Details_View_Apply')}
            </button>
            <button type="button" disabled={readOnly} className="btn btn-default" onClick={this.dismiss}>
              {i18n.getString('Details_View_Dismiss')}
            </button>
          </div>
        </div>
        <div className={`separator-col details-separator-col-2 ${rowClass}`} />
        <div className={`details-estimates-col ${rowClass}`}>
          {suggested &&
            <EstimationTile {...this.props.estimates} />}
        </div>
      </React.Fragment>
    );
  }
}
